/**
 * Empty-space trimming for footer strip logos (docs/footer-logo-strip.md).
 *
 * Logo files often carry padding: a transparent margin, or a white box
 * around the mark. Sized by its file bounds, a padded logo renders smaller
 * than its neighbours. This finds the box that actually holds ink so the
 * strip can size and crop to it.
 *
 * Runs in the browser at render time, not at upload, so it covers every
 * strip logo: partner logos, the agency's own (theme.logoUrl, never
 * uploaded through the agent), and logos added before this existed.
 */

/** Content box as fractions (0..1) of the image's natural width/height. */
export interface TrimBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Channel distance from the background that still counts as background. */
const COLOR_TOLERANCE = 24;
/** Alpha at or below this is empty. */
const ALPHA_EMPTY = 16;
/** Longest side the image is rasterised at for measuring. */
const MEASURE_MAX = 400;

/**
 * Finds the ink box in RGBA pixels. Padding is either transparent (every
 * corner transparent) or a near-white box (every corner near-white), since
 * both vanish against the white footer. A dark or coloured tile is part of
 * the logo, never padding, and any other image comes back untrimmed (null).
 * Also null when nothing is ink or the ink already fills the image.
 */
export function trimBounds(data: Uint8ClampedArray, width: number, height: number): TrimBox | null {
  if (width <= 0 || height <= 0 || data.length < width * height * 4) return null;
  const at = (x: number, y: number) => (y * width + x) * 4;
  const corners = [at(0, 0), at(width - 1, 0), at(0, height - 1), at(width - 1, height - 1)];
  const transparent = corners.every((i) => data[i + 3]! <= ALPHA_EMPTY);
  const bg = corners[0]!;
  const sameAsBg = (i: number) =>
    data[i + 3]! > 255 - ALPHA_EMPTY &&
    Math.abs(data[i]! - data[bg]!) <= COLOR_TOLERANCE &&
    Math.abs(data[i + 1]! - data[bg + 1]!) <= COLOR_TOLERANCE &&
    Math.abs(data[i + 2]! - data[bg + 2]!) <= COLOR_TOLERANCE;
  const nearWhite = (i: number) => data[i]! >= 235 && data[i + 1]! >= 235 && data[i + 2]! >= 235;
  if (!transparent && !(nearWhite(bg) && corners.every(sameAsBg))) return null;

  const isInk = transparent ? (i: number) => data[i + 3]! > ALPHA_EMPTY : (i: number) => data[i + 3]! > ALPHA_EMPTY && !sameAsBg(i);
  let left = width;
  let right = -1;
  let top = height;
  let bottom = -1;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (!isInk(at(x, y))) continue;
      if (x < left) left = x;
      if (x > right) right = x;
      if (y < top) top = y;
      if (y > bottom) bottom = y;
    }
  }
  if (right < 0) return null;
  if (left === 0 && top === 0 && right === width - 1 && bottom === height - 1) return null;
  return {
    x: left / width,
    y: top / height,
    width: (right - left + 1) / width,
    height: (bottom - top + 1) / height,
  };
}

const cache = new Map<string, TrimBox | null>();

/**
 * The loaded image's ink box, cached by src (every slide repeats the same
 * footer). null = draw it untrimmed: no padding found, or the canvas could
 * not be read (a cross-origin logo taints it; jsdom has no canvas).
 */
export function measureTrim(img: HTMLImageElement): TrimBox | null {
  const key = img.currentSrc || img.src;
  if (cache.has(key)) return cache.get(key)!;
  let box: TrimBox | null = null;
  try {
    const scale = Math.min(1, MEASURE_MAX / Math.max(img.naturalWidth, img.naturalHeight));
    const w = Math.max(1, Math.round(img.naturalWidth * scale));
    const h = Math.max(1, Math.round(img.naturalHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (ctx) {
      ctx.drawImage(img, 0, 0, w, h);
      box = trimBounds(ctx.getImageData(0, 0, w, h).data, w, h);
    }
  } catch {
    box = null;
  }
  cache.set(key, box);
  return box;
}
